import React, { useEffect, useState } from 'react';
import { LEADS_TEST_MODE, LEADS_TEST_VISUALS, LEAD_CREATE_ROLES, OPEN_STATUSES, stockLabel } from './config';
import { L } from './ui';
import Bandeja from './Bandeja';
import MiCartera from './MiCartera';
import LeadDetail from './LeadDetail';
import NewLeadForm from './NewLeadForm';
import LeadCard from './LeadCard';
import Supervision, { DEFAULT_PANEL } from './Supervision';

// Ventana del módulo de Leads. Recibe los datos en vivo de useLeadsLive (los carga el tablero)
// y el cliente `supabase` por props (no se importa App.js desde aquí).
export default function LeadsModal({ supabase, data, role, currentUserId, vehicles, columns, isMobile, initialLeadId, initialVehicleId, onClose }) {
  const { access, leads, profiles, accessRows, loading, error, afterChange } = data;
  const showBandeja = access.can_claim || access.supervisor;
  const canCreate = LEAD_CREATE_ROLES.includes(role) && access.ok;
  const unassigned = leads.filter(l => !l.assigned_to && OPEN_STATUSES.includes(l.status)).length;
  const mineOpen = leads.filter(l => l.assigned_to === currentUserId && OPEN_STATUSES.includes(l.status)).length;

  const [tab, setTab] = useState(() => (showBandeja && unassigned > 0 ? 'bandeja' : access.supervisor ? 'panel' : 'cartera'));
  const [panel, setPanel] = useState(DEFAULT_PANEL);
  // Vistas: lista (pestañas), 'vehicle' (leads de un coche, desde su ficha), 'detail' (un lead) y 'new' (alta).
  const [view, setView] = useState(() => (
    initialLeadId ? { type: 'detail', id: initialLeadId }
      : initialVehicleId ? { type: 'vehicle', id: initialVehicleId }
        : { type: 'list' }
  ));
  const [notice, setNotice] = useState('');

  // Esc y el clic fuera de la ventana solo cierran desde las listas, para no perder un formulario a medias.
  const closableOutside = view.type === 'list' || view.type === 'vehicle';
  useEffect(() => {
    if (!closableOutside) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, closableOutside]);

  const openLead = (id) => { setNotice(''); setView(v => ({ type: 'detail', id, from: v.type === 'vehicle' ? v : null })); };
  const backToList = () => setView(v => (v.from ? v.from : { type: 'list' }));
  const nameOf = (id) => (profiles.find(p => p.id === id)?.full_name || '').trim() || 'Usuario';
  const vehicle = view.type === 'vehicle' ? (vehicles || []).find(v => v.id === view.id) : null;
  const lead = view.type === 'detail' ? leads.find(l => l.id === view.id) : null;

  const tabs = [
    showBandeja && { key: 'bandeja', label: `${isMobile ? '' : '📥 '}Bandeja${unassigned ? ` (${unassigned})` : ''}` },
    { key: 'cartera', label: `${isMobile ? '' : '🗂️ '}Mi cartera${mineOpen ? ` (${mineOpen})` : ''}` },
    access.supervisor && { key: 'panel', label: `${isMobile ? '' : '🛡️ '}Supervisión` }
  ].filter(Boolean);

  return (
    <div style={{ ...L.overlay, alignItems: isMobile ? 'stretch' : 'center' }} onClick={e => { if (closableOutside && e.target === e.currentTarget) onClose(); }}>
      <div style={isMobile ? L.modalMobile : L.modal} role="dialog" aria-label="Leads">
        <div style={L.head}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <h3 style={L.title}>📇 Leads</h3>
            {canCreate && view.type !== 'new' && (
              <button type="button" style={{ ...L.button, padding: '7px 12px', marginLeft: 'auto' }} onClick={() => { setNotice(''); setView({ type: 'new' }); }}>
                ➕ Nuevo lead
              </button>
            )}
            <button type="button" onClick={onClose} aria-label="Cerrar" style={{ ...L.closeBtn, marginLeft: canCreate && view.type !== 'new' ? 0 : 'auto' }}>✕</button>
          </div>
          {LEADS_TEST_MODE && LEADS_TEST_VISUALS && (
            <div style={{ ...L.warn, margin: '0 0 10px', padding: '6px 10px' }}>
              🧪 <strong>Modo pruebas:</strong> no introduzcas datos reales de clientes hasta tener autorización de Flexicar.
            </div>
          )}
          {view.type === 'list' && (
            <div role="tablist" style={{ display: 'flex', gap: '4px', overflowX: 'auto' }}>
              {tabs.map(t => (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.key}
                  onClick={() => setTab(t.key)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                    padding: '8px 12px', fontSize: '0.9rem', fontWeight: tab === t.key ? 700 : 500,
                    color: tab === t.key ? 'var(--t-f59e0b)' : 'var(--t-9ca3af)',
                    borderBottom: `2px solid ${tab === t.key ? 'var(--l-f59e0b)' : 'transparent'}`
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={L.body}>
          {error && <div style={L.error}>No se han podido cargar los leads: {error}</div>}
          {notice && view.type === 'list' && <div style={L.ok}>{notice}</div>}
          {loading ? (
            <p style={L.muted}>Cargando leads…</p>
          ) : view.type === 'new' ? (
            <NewLeadForm
              supabase={supabase}
              access={access}
              vehicles={vehicles}
              columns={columns}
              onCancel={backToList}
              onCreated={(id, claimed) => {
                afterChange();
                if (claimed && id) { setView({ type: 'detail', id }); return; }
                setTab(showBandeja ? 'bandeja' : 'cartera');
                setNotice('Lead registrado. Ya está en la Bandeja y se ha avisado a los comerciales.');
                backToList();
              }}
            />
          ) : view.type === 'detail' ? (
            lead ? (
              <LeadDetail
                supabase={supabase}
                lead={lead}
                access={access}
                currentUserId={currentUserId}
                profiles={profiles}
                accessRows={accessRows}
                vehicles={vehicles}
                columns={columns}
                afterChange={afterChange}
                onBack={backToList}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 12px' }}>
                <p style={{ color: 'var(--t-cbd5e1)' }}>No puedes ver este lead: puede que lo haya cogido otra persona o que ya no exista.</p>
                <button type="button" style={L.secondary} onClick={backToList}>← Volver</button>
              </div>
            )
          ) : view.type === 'vehicle' ? (
            <div style={{ display: 'grid', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <button type="button" onClick={() => setView({ type: 'list' })} style={{ ...L.secondary, padding: '6px 10px' }}>← Todos</button>
                <h4 style={{ ...L.title, fontSize: '1.05rem' }}>
                  🚗 {vehicle ? `${vehicle.brand} ${vehicle.model}${vehicle.plate ? ` · ${vehicle.plate}` : ''}` : 'Coche'}
                </h4>
              </div>
              {leads.filter(l => l.vehicle_id === view.id).length === 0 ? (
                <p style={L.muted}>No hay leads de este coche que puedas ver.</p>
              ) : leads.filter(l => l.vehicle_id === view.id)
                .sort((a, b) => new Date(b.received_at) - new Date(a.received_at))
                .map(l => (
                  <LeadCard key={l.id} lead={l} mode={l.assigned_to ? 'cartera' : 'bandeja'} stock={stockLabel(l, vehicles, columns)}
                    ownerName={l.assigned_to ? nameOf(l.assigned_to) : null} onOpen={() => openLead(l.id)} />
                ))}
            </div>
          ) : tab === 'panel' && access.supervisor ? (
            <Supervision supabase={supabase} leads={leads} profiles={profiles} accessRows={accessRows} vehicles={vehicles}
              columns={columns} afterChange={afterChange} onOpen={openLead} state={panel} setState={setPanel} />
          ) : tab === 'bandeja' && showBandeja ? (
            <Bandeja supabase={supabase} leads={leads} access={access} profiles={profiles} accessRows={accessRows}
              vehicles={vehicles} columns={columns} afterChange={afterChange} onOpen={openLead} />
          ) : (
            <MiCartera leads={leads} currentUserId={currentUserId} vehicles={vehicles} columns={columns} onOpen={openLead} />
          )}
        </div>
      </div>
    </div>
  );
}
